import { useEffect } from "react";

import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import { Button } from "@ps-design-system/components/Button";
import XIcon from "@ps-design-system/icons/X/X24";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import {
  trackPromotedByHostBannerClicked,
  trackPromotedByHostBannerDismissed,
  trackPromotedByHostBannerViewed,
} from "../analytics/host-hub-analytics";
import promotedByHostDesktopAsset from "../assets/promoted-by-host-desktop.png";
import promotedByHostMobileAsset from "../assets/promoted-by-host-mobile.png";

interface HostHubPromotedByHostBannerProps {
  dismiss: () => void;
}

const HostHubPromotedByHostBanner = ({
  dismiss,
}: HostHubPromotedByHostBannerProps) => {
  const ssoid = useSelector(selectProfileSSOid);

  useEffect(() => {
    trackPromotedByHostBannerViewed({ ssoid });
  }, [ssoid]);

  const handleCtaClick = () => {
    trackPromotedByHostBannerClicked({ ssoid });
    window.open(
      "https://hosts.peerspace.com/promoted-by-host",
      "_blank",
      "noopener",
    );
  };

  const handleDismiss = () => {
    trackPromotedByHostBannerDismissed({ ssoid });
    dismiss();
  };

  return (
    <div className="tw-relative tw-flex tw-flex-col tw-gap-[24px] tw-overflow-hidden tw-rounded-[12px] tw-bg-brand-black tw-p-[24px] tw-text-brand-white lg:tw-flex-row lg:tw-items-center lg:tw-gap-[32px] lg:tw-p-[32px]">
      <button
        aria-label="Dismiss Promoted by Host banner"
        className="tw-absolute tw-right-[16px] tw-top-[16px] tw-flex tw-h-[32px] tw-w-[32px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-brand-white"
        onClick={handleDismiss}
        type="button"
      >
        <XIcon />
      </button>

      <img
        alt=""
        aria-hidden="true"
        className="tw-mx-auto tw-block tw-w-full tw-max-w-[240px] lg:tw-hidden"
        src={promotedByHostMobileAsset}
      />

      <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-[16px] lg:tw-pr-0">
        <div className="tw-flex tw-flex-col tw-gap-[8px]">
          <h2 className="tw-my-0 tw-text-[24px] tw-font-semibold tw-leading-[32px] tw-text-brand-white">
            <FormattedMessage
              defaultMessage="Promote your listing"
              id="host_hub.promoted_by_host_banner.title"
            />
          </h2>
          <p className="tw-my-0 tw-text-[14px] tw-leading-[20px] tw-text-brand-white">
            <FormattedMessage
              defaultMessage="Boost your visibility with the Promoted by Host program. Appear in the top 3 search results with premium placement. You'll receive exclusive analytics showing views, clicks, and bookings."
              id="host_hub.promoted_by_host_banner.description"
            />
          </p>
        </div>
        <div className="tw-flex">
          <Button
            appearance="white"
            className="tw-w-full lg:tw-w-auto"
            onClick={handleCtaClick}
            variant="default"
          >
            <FormattedMessage
              defaultMessage="Boost my listing"
              id="host_hub.promoted_by_host_banner.cta"
            />
          </Button>
        </div>
      </div>

      <img
        alt=""
        aria-hidden="true"
        className="tw-hidden tw-max-w-[200px] lg:tw-block"
        src={promotedByHostDesktopAsset}
      />
    </div>
  );
};

export default HostHubPromotedByHostBanner;
